// Wannier functions (sub-module of Tight Binding) in a project: 1 the source (a finished DFT run, your own Wannier90
// interface files, or a finished Wannier90 model), 2 the k-mesh and bands, 3 the projections, 4 the windows (on the DFT
// bands, coloured by projectability), 5 the run, 6 the results (model against DFT, spreads, centres, decay; on to NEGF
// Transport and Properties). Routes B and C skip 2-4: the .win (or the model files) already holds them.
import { Alert, Card, Group, Loader, Stack, Stepper, Switch } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { get, patch, type Job, type ProjectDetail } from "../../lib/api";
import { useMe } from "../../lib/hooks";
import type { DftResult } from "../../lib/skfitter";
import { buildRecipe, defaultWannierMemory, seedFromName, wannierCheck, type WannierMemory } from "../../lib/wannier";
import { MeshStep, ProjectionsStep, WindowsStep } from "./RecipeSteps";
import { ResultsStep } from "./ResultsStep";
import { RunStep } from "./RunStep";
import { SourceStep } from "./SourceStep";
import { WannierExampleBanner } from "../WannierExamples";
import { TeachNote } from "./Help";

const short = (s: string) => (s.length > 28 ? `${s.slice(0, 26).replace(/[ ,(]+$/, "")}…` : s);

export type Remember = (next: Partial<WannierMemory>) => void;

/** The DFT run's data (elements, lattice, sites, bands) from its job. */
export function useDftRun(jobId: string | undefined) {
  const q = useQuery({ queryKey: ["job", jobId], queryFn: () => get<Job>(`/jobs/${jobId}`), enabled: !!jobId, staleTime: 60_000 });
  return { ...q, dft: q.data?.status === "succeeded" ? (q.data.result as DftResult & { meta: { sites?: { symbol: string; frac: number[] }[] } }) : undefined };
}

export function WannierWorkspace({ project }: { project: ProjectDetail }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const saved = project.settings?.wannier as Partial<WannierMemory> | undefined;
  const [mem, setMem] = useState<WannierMemory>(() => ({ ...defaultWannierMemory(seedFromName(project.name)), ...(saved ?? {}) }));
  const [step, setStep] = useState<number>(saved?.job ? 5 : saved?.dft_run || saved?.files || saved?.model ? 1 : 0);

  const latest = useRef(mem);
  latest.current = mem;
  const remember = useCallback<Remember>((next) => {
    const merged = { ...latest.current, ...next };
    latest.current = merged;
    setMem(merged);
    patch(`/projects/${project.id}`, { settings: { ...(project.settings ?? {}), wannier: merged } })
      .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] }))
      .catch(() => undefined);
  }, [project.id, project.settings, qc]);

  // the recipe's check (route A), as the steps change it
  const recipe = mem.source === "dft" && mem.dft_run ? buildRecipe(mem) : null;
  const [debounced] = useDebouncedValue(recipe ? JSON.stringify(recipe) : "", 400);
  const check = useQuery({ queryKey: ["wannier-check", debounced], queryFn: () => wannierCheck(JSON.parse(debounced), project.id),
    enabled: !!debounced, placeholderData: (p) => p });
  const dft = useDftRun(mem.source === "dft" ? mem.dft_run : undefined);

  // a run finished: show its results
  useEffect(() => { if (saved?.job && saved.job !== mem.job) setMem((m) => ({ ...m, job: saved.job })); }, [saved?.job]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!me) return <Card withBorder><Loader size="sm" /></Card>;
  const limits = project.limits ?? me.limits;
  if (!limits["wannier.enabled"])
    return <Alert color="orange" title="Not included in your plan">Wannier functions are not part of your plan.</Alert>;

  const routeA = mem.source === "dft";
  const ready = routeA ? !!mem.dft_run : mem.source === "files" ? !!mem.files?.win : !!mem.model?.hr;
  const fromWin = "set in your .win";
  const exampleId = (project.settings as { example?: string } | undefined)?.example;
  const fromTry = mem.from_example;
  return (
    <Stack gap="md">
    {(exampleId || fromTry) && <WannierExampleBanner exampleId={(exampleId ?? fromTry?.id)!} fromTry={exampleId ? undefined : fromTry} />}
    <Card withBorder>
      <Group justify="flex-end" mb={4}>
        <Switch size="xs" label="Teaching notes" checked={!!mem.teach} onChange={(e) => remember({ teach: e.currentTarget.checked })}
          data-testid="teach-switch" />
      </Group>
      <Stepper active={step} onStepClick={setStep} size="sm" allowNextStepsSelect={false} data-testid="wannier-steps">
        <Stepper.Step label="Source" description={mem.source === "dft" ? short(mem.dft_label ?? "a DFT run") : mem.source === "files" ? "interface files" : "model files"}>
          <TeachNote step="source" on={!!mem.teach} />
          <SourceStep project={project} mem={mem} remember={remember} onNext={() => setStep(routeA ? 1 : 4)} />
        </Stepper.Step>
        <Stepper.Step label="Mesh" description={routeA ? `${mem.mesh.join("×")} k-points` : fromWin} disabled={!routeA || !ready}>
          <TeachNote step="mesh" on={!!mem.teach} />
          {routeA && <MeshStep project={project} mem={mem} remember={remember} dft={dft.dft} check={check.data} limits={limits}
            onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        </Stepper.Step>
        <Stepper.Step label="Projections" description={routeA ? (check.data?.num_wann ? `${check.data.num_wann} Wannier functions` : "orbitals per element") : fromWin}
          disabled={!routeA || !ready}>
          <TeachNote step="projections" on={!!mem.teach} />
          {routeA && <ProjectionsStep mem={mem} remember={remember} dft={dft.dft} check={check.data}
            onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        </Stepper.Step>
        <Stepper.Step label="Windows" description={routeA ? (mem.windows ? "disentanglement" : "isolated bands") : fromWin} disabled={!routeA || !ready}>
          <TeachNote step="windows" on={!!mem.teach} />
          {routeA && <WindowsStep project={project} mem={mem} remember={remember} check={check.data}
            onBack={() => setStep(2)} onNext={() => setStep(4)} />}
        </Stepper.Step>
        <Stepper.Step label="Run" description={mem.source === "model" ? "build the model" : "Wannier90"} disabled={!ready}>
          <TeachNote step="run" on={!!mem.teach} />
          <RunStep project={project} mem={mem} remember={remember} check={check.data} checking={check.isFetching} limits={limits}
            onBack={() => setStep(routeA ? 3 : 0)} onDone={() => setStep(5)} />
        </Stepper.Step>
        <Stepper.Step label="Results" description="model, spreads, quality" disabled={!mem.job}>
          <TeachNote step="results" on={!!mem.teach} />
          {mem.job && <ResultsStep project={project} mem={mem} remember={remember} />}
        </Stepper.Step>
      </Stepper>
    </Card>
    </Stack>
  );
}
